import { getColumnDataFieldKey } from '../../../../../shared/utils/queryBuilder';
import type { IColumn } from '../../types/IColumn';
import { CURRENCY_DATASOURCES, LOCAL_CURRENCY_CALCULATED_DATASOURCES } from '../eo_mapping/datasources';
import {
  COLUMN_HEADER_STYLE,
  COLUMN_METADATA,
  FORMULAS,
  GRADIENT_COLUMNS,
  GROUPS,
  METADATA_TYPE,
  POPOVERS,
} from './eo_constants';
import { getColumnDisplay, getUnderlyingDataSources } from './eo_helpers';
import type { ColumnType } from './eo_types';

export type BuildColumnProps = {
  type: ColumnType;
  key: string;
  sources?: any;
  formulas?: any;
  addAllFormulas?: boolean;
  extensions?: Record<string, any>;
  localCurrency?: string;
  overrides?: Partial<IColumn>;
};

export const buildColumn = (props: BuildColumnProps): IColumn => {
  const { type, key, sources, extensions, addAllFormulas, formulas, localCurrency, overrides } = props;

  const display = getColumnDisplay(key);
  const reportColumnKey = getColumnDataFieldKey(key);
  const reportDataKey = reportColumnKey || key;

  const column: IColumn = {
    reportColumnKey,
    reportDataKey,
    type,
    key,
    display,
  };

  if (METADATA_TYPE[type]) {
    Object.assign(column, { ...METADATA_TYPE[type] });
  }

  // set column metadata based on column key. Can overwrite METADATA_TYPE properties
  if (COLUMN_METADATA[key]) {
    Object.assign(column, { ...COLUMN_METADATA[key] });
  }

  if (extensions) {
    Object.assign(column, { extensions });
  }

  if ((addAllFormulas || formulas?.includes(key)) && FORMULAS[key]) {
    column.formulas = FORMULAS[key];
  }

  const popover = POPOVERS[key];
  if (popover) {
    column.popover = popover;
  }

  const group = GROUPS[key] || extensions?.GROUPS?.[key];
  if (group) {
    column.group = group;
  }

  const className = COLUMN_HEADER_STYLE[key];
  if (className) {
    column.className = className;
  }

  if (key in GRADIENT_COLUMNS) {
    column.gradient = true;
  }

  const dataSources = getUnderlyingDataSources(type, sources, key);
  if (dataSources) {
    column.dataSources = dataSources;
  }

  const isLegacyLocalCurrencyCalculatedDatasource = LOCAL_CURRENCY_CALCULATED_DATASOURCES.includes(key);
  const isCurrencyDatasource = extensions?.CURRENCY_SWITCHER && CURRENCY_DATASOURCES.includes(key);
  const isColumnCurrencyType = extensions?.CURRENCY_SWITCHER && type === 'currency_formula';
  const isCustomCalculationFormattedToCurrency = extensions?.CURRENCY_SWITCHER && extensions?.FORMAT === 'currency';
  const shouldSetLocalCurrency =
    localCurrency &&
    (isLegacyLocalCurrencyCalculatedDatasource ||
      isCurrencyDatasource ||
      isColumnCurrencyType ||
      isCustomCalculationFormattedToCurrency);

  if (shouldSetLocalCurrency) {
    column.localCurrency = localCurrency;
  }

  if (overrides) {
    Object.assign(column, overrides);
  }

  return column;
};
